import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CheckCircledIcon } from '@radix-ui/react-icons';
import { apiUtils } from "../../../utils/apiUtils";
import useRepGuardClients from "./useRepGuardClients";
import { useUser } from "../../../contexts/UserContext";

const STATUS_OPTIONS = [
  { value: "all", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
  { value: "changes_requested", label: "Changes Requested" },
];

function formatCategory(value) {
  return (value || "other")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatDate(value) {
  if (!value) {
    return "Unknown";
  }

  return new Date(value).toLocaleString();
}

export default function RepGuardReviewQueue() {
  const { user } = useUser();
  const [statusFilter, setStatusFilter] = useState("pending");
  const [clientFilter, setClientFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [actionLoadingId, setActionLoadingId] = useState("");
  const [error, setError] = useState("");
  const [queueData, setQueueData] = useState({ reviews: [], summary: {} });
  const [reviewNotes, setReviewNotes] = useState({});
  const [reviewers, setReviewers] = useState([]);
  const [assignmentState, setAssignmentState] = useState({});
  const [permissions, setPermissions] = useState({
    canReviewDrafts: false,
    role: "viewer",
  });
  const {
    clients,
    clientError,
  } = useRepGuardClients();

  const loadReviewers = useCallback(async () => {
    try {
      const response = await apiUtils.get("/api/users/reviewers");
      setReviewers(response.data?.data?.reviewers || []);
    } catch (_error) {
      setReviewers([]);
    }
  }, []);

  const summaryItems = useMemo(() => [
    { label: "Pending", value: queueData.summary?.pending || 0 },
    { label: "Approved", value: queueData.summary?.approved || 0 },
    { label: "Rejected", value: queueData.summary?.rejected || 0 },
    { label: "Changes Requested", value: queueData.summary?.changes_requested || 0 },
  ], [queueData.summary]);

  const loadReviews = useCallback(async (status = statusFilter) => {
    try {
      setLoading(true);
      setError("");
      const response = await apiUtils.get("/api/extension/reviews", {
        params: {
          status,
          clientId: clientFilter !== "all" ? clientFilter : undefined,
        },
      });

      setQueueData({
        reviews: response.data?.data?.reviews || [],
        summary: response.data?.data?.summary || {},
      });
      setAssignmentState(
        (response.data?.data?.reviews || []).reduce((acc, review) => {
          acc[review._id] = {
            reviewerIds: (review.assignedReviewers || []).map((reviewer) => String(reviewer.userId)),
            requiredApprovals: review.requiredApprovals || 1,
          };
          return acc;
        }, {})
      );
      setPermissions(response.data?.data?.permissions || {
        canReviewDrafts: false,
        role: "viewer",
      });
    } catch (loadError) {
      setError(loadError.message || "Failed to load RepGuard review queue.");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, clientFilter]);

  useEffect(() => {
    loadReviews(statusFilter);
  }, [loadReviews, statusFilter]);

  useEffect(() => {
    loadReviewers();
  }, [loadReviewers]);

  const handleDecision = async (reviewId, status) => {
    try {
      setActionLoadingId(`${reviewId}:${status}`);
      setError("");
      await apiUtils.post(`/api/extension/reviews/${reviewId}/decision`, {
        status,
        reviewNotes: reviewNotes[reviewId] || "",
      });
      await loadReviews(statusFilter);
    } catch (decisionError) {
      setError(decisionError.message || "Failed to update review status.");
    } finally {
      setActionLoadingId("");
    }
  };

  const handleAssignmentChange = (reviewId, reviewerId) => {
    setAssignmentState((prev) => {
      const current = prev[reviewId] || { reviewerIds: [], requiredApprovals: 1 };
      const alreadySelected = current.reviewerIds.includes(reviewerId);
      const reviewerIds = alreadySelected
        ? current.reviewerIds.filter((id) => id !== reviewerId)
        : [...current.reviewerIds, reviewerId];

      return {
        ...prev,
        [reviewId]: {
          ...current,
          reviewerIds,
          requiredApprovals: Math.max(1, Math.min(current.requiredApprovals, reviewerIds.length || 1)),
        },
      };
    });
  };

  const handleAssignReviewers = async (reviewId) => {
    try {
      setActionLoadingId(`${reviewId}:assign`);
      setError("");
      const payload = assignmentState[reviewId] || { reviewerIds: [], requiredApprovals: 1 };
      await apiUtils.post(`/api/extension/reviews/${reviewId}/assign`, payload);
      await loadReviews(statusFilter);
    } catch (assignError) {
      setError(assignError.message || "Failed to assign reviewers.");
    } finally {
      setActionLoadingId("");
    }
  };

  const canCurrentUserReview = (review) => {
    if (!permissions.canReviewDrafts) {
      return false;
    }

    if (!review.assignedReviewers || review.assignedReviewers.length === 0) {
      return true;
    }

    return review.assignedReviewers.some(
      (reviewer) => String(reviewer.userId) === String(user?.id)
    );
  };

  return (
    <div className="space-y-6">
      <div className="border-t border-border pt-6 first:border-0 first:pt-0">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-foreground">Draft Review Queue</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Review drafts escalated from RepGuard and decide whether they should be approved, rejected, or sent back for edits.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <label className="text-sm font-medium text-foreground">Client</label>
            <select
              value={clientFilter}
              onChange={(event) => setClientFilter(event.target.value)}
              className="rounded-lg border border-input px-3 py-2 text-sm"
            >
              <option value="all">All clients</option>
              {clients.map((client) => (
                <option key={client._id} value={client._id}>
                  {client.name}
                </option>
              ))}
            </select>
            <label className="text-sm font-medium text-foreground">Filter</label>
            <select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
              className="rounded-lg border border-input px-3 py-2 text-sm"
            >
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-6 grid gap-4 md:grid-cols-4">
          {summaryItems.map((item) => (
            <div key={item.label} className="rounded-xl border border-border bg-muted p-4">
              <div className="text-sm text-muted-foreground">{item.label}</div>
              <div className="mt-2 text-lg font-semibold text-foreground">{item.value}</div>
            </div>
          ))}
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-red-500/10 dark:border-red-500/30 dark:text-red-200">
          {error}
        </div>
      )}
      {!permissions.canReviewDrafts && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:bg-amber-500/10 dark:border-amber-500/30 dark:text-amber-200">
          Your role is <span className="font-semibold">{permissions.role}</span>. You can view draft reviews, but only admins, managers, and analysts can make review decisions.
        </div>
      )}
      {clientError && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-red-500/10 dark:border-red-500/30 dark:text-red-200">
          {clientError}
        </div>
      )}

      {loading ? (
        <div className="border-t border-border pt-6 first:border-0 first:pt-0">
          <p className="text-muted-foreground">Loading review queue...</p>
        </div>
      ) : queueData.reviews.length === 0 ? (
        <div className="bg-card border border-border rounded-xl p-10 text-center">
          <CheckCircledIcon className="mx-auto mb-3 h-8 w-8 text-green-600" />
          <h3 className="text-lg font-semibold text-foreground">No review requests found</h3>
          <p className="mt-2 text-sm text-muted-foreground">
            Drafts escalated from the extension will appear here.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {queueData.reviews.map((review) => {
            const statusBadgeClasses = {
              pending: "bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300",
              approved: "bg-green-100 text-green-800 dark:bg-green-500/15 dark:text-green-300",
              rejected: "bg-red-100 text-red-800 dark:bg-red-500/15 dark:text-red-300",
              changes_requested: "bg-brand-100 text-brand-800 dark:bg-brand-500/15 dark:text-brand-300",
            };

            return (
              <div key={review._id} className="border-t border-border pt-6 first:border-0 first:pt-0">
                <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                  <div className="space-y-3 flex-1">
                    <div className="flex flex-wrap items-center gap-3">
                      <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${statusBadgeClasses[review.status] || "bg-muted text-foreground"}`}>
                        {review.status.replace(/_/g, " ")}
                      </span>
                      <span className="text-sm text-muted-foreground">
                        {review.clientName || "Unknown Client"} • {review.platform?.toUpperCase()} • Submitted {formatDate(review.createdAt)}
                      </span>
                    </div>

                    <div>
                      <p className="text-sm font-medium text-foreground">Draft preview</p>
                      <p className="mt-1 text-foreground whitespace-pre-wrap">
                        {review.draftPreview || review.draftText || "No draft preview available."}
                      </p>
                    </div>

                    <div className="grid gap-3 md:grid-cols-4">
                      <div className="rounded-lg bg-muted p-3">
                        <div className="text-xs uppercase tracking-wide text-muted-foreground">Decision</div>
                        <div className="mt-1 text-sm font-semibold text-foreground">
                          {review.submittedDecision.replace(/_/g, " ")}
                        </div>
                      </div>
                      <div className="rounded-lg bg-muted p-3">
                        <div className="text-xs uppercase tracking-wide text-muted-foreground">Risk score</div>
                        <div className="mt-1 text-sm font-semibold text-foreground">
                          {review.analysis?.riskScore ?? 0}/100
                        </div>
                      </div>
                      <div className="rounded-lg bg-muted p-3">
                        <div className="text-xs uppercase tracking-wide text-muted-foreground">Category</div>
                        <div className="mt-1 text-sm font-semibold text-foreground">
                          {formatCategory(review.analysis?.category)}
                        </div>
                      </div>
                      <div className="rounded-lg bg-muted p-3">
                        <div className="text-xs uppercase tracking-wide text-muted-foreground">Severity</div>
                        <div className="mt-1 text-sm font-semibold text-foreground">
                          {review.analysis?.severity || "low"}
                        </div>
                      </div>
                    </div>

                    <div className="text-sm text-foreground">
                      <div><span className="font-semibold">AI reason:</span> {review.analysis?.reason || "No reason provided."}</div>
                      <div className="mt-1"><span className="font-semibold">Review request:</span> {review.requestReason || "No reason provided."}</div>
                      <div className="mt-1"><span className="font-semibold">Required approvals:</span> {review.requiredApprovals || 1}</div>
                      <div className="mt-1">
                        <span className="font-semibold">Assigned reviewers:</span>{" "}
                        {review.assignedReviewers?.length
                          ? review.assignedReviewers.map((reviewer) => `${reviewer.name || reviewer.email} (${reviewer.status})`).join(", ")
                          : "None assigned"}
                      </div>
                      {review.reviewNotes && (
                        <div className="mt-1"><span className="font-semibold">Review notes:</span> {review.reviewNotes}</div>
                      )}
                    </div>
                  </div>

                  <div className="w-full xl:w-80">
                    <label className="text-sm font-medium text-foreground block mb-2">
                      Review notes
                    </label>
                    <textarea
                      rows={5}
                      value={reviewNotes[review._id] ?? review.reviewNotes ?? ""}
                      onChange={(event) =>
                        setReviewNotes((prev) => ({
                          ...prev,
                          [review._id]: event.target.value,
                        }))
                      }
                      className="w-full rounded-lg border border-input px-3 py-2"
                      placeholder="Explain the decision for audit history and the extension user."
                    />

                    {permissions.canAssignReviewers && (
                      <div className="mt-4 rounded-lg border border-border p-3">
                        <div className="text-sm font-semibold text-foreground">Reviewer assignment</div>
                        <div className="mt-2 grid gap-2">
                          {reviewers.map((reviewer) => (
                            <label key={reviewer._id} className="flex items-center gap-2 text-sm text-foreground">
                              <input
                                type="checkbox"
                                checked={(assignmentState[review._id]?.reviewerIds || []).includes(reviewer._id)}
                                onChange={() => handleAssignmentChange(review._id, reviewer._id)}
                              />
                              <span>{reviewer.name} ({reviewer.role})</span>
                            </label>
                          ))}
                        </div>
                        <label className="mt-3 block text-sm text-foreground">
                          Required approvals
                          <input
                            type="number"
                            min="1"
                            max={Math.max((assignmentState[review._id]?.reviewerIds || []).length || 1, 1)}
                            value={assignmentState[review._id]?.requiredApprovals || 1}
                            onChange={(event) =>
                              setAssignmentState((prev) => ({
                                ...prev,
                                [review._id]: {
                                  ...(prev[review._id] || { reviewerIds: [], requiredApprovals: 1 }),
                                  requiredApprovals: Number(event.target.value),
                                },
                              }))
                            }
                            className="mt-2 w-full rounded-lg border border-input px-3 py-2"
                          />
                        </label>
                        <button
                          type="button"
                          onClick={() => handleAssignReviewers(review._id)}
                          disabled={Boolean(actionLoadingId)}
                          className="mt-3 rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-neutral-400"
                        >
                          {actionLoadingId === `${review._id}:assign` ? "Saving..." : "Save reviewer assignment"}
                        </button>
                      </div>
                    )}

                    {review.status === "pending" && canCurrentUserReview(review) ? (
                      <div className="mt-4 grid gap-2">
                        <button
                          type="button"
                          onClick={() => handleDecision(review._id, "approved")}
                          disabled={Boolean(actionLoadingId)}
                          className="rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:bg-green-300"
                        >
                          {actionLoadingId === `${review._id}:approved` ? "Approving..." : "Approve draft"}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDecision(review._id, "changes_requested")}
                          disabled={Boolean(actionLoadingId)}
                          className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-brand-300"
                        >
                          {actionLoadingId === `${review._id}:changes_requested` ? "Saving..." : "Request changes"}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDecision(review._id, "rejected")}
                          disabled={Boolean(actionLoadingId)}
                          className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-red-300"
                        >
                          {actionLoadingId === `${review._id}:rejected` ? "Rejecting..." : "Reject draft"}
                        </button>
                      </div>
                    ) : (
                      <div className="mt-4 text-sm text-muted-foreground">
                        {review.status === "pending"
                          ? "Awaiting a reviewer decision."
                          : <>Reviewed {review.reviewedAt ? formatDate(review.reviewedAt) : "previously"} by{" "}
                              <span className="font-medium text-foreground">
                                {review.reviewedByEmail || "unknown reviewer"}
                              </span></>}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
